import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePortal } from '../../contexts/PortalAuthContext';

interface PortalLayoutProps {
  children: React.ReactNode;
}

/**
 * PORTAL-09: Branded shell for all client portal pages.
 * NO DashboardLayout, NO CRM sidebar, NO internal navigation links.
 * Shows WTF Amplify logo + "Client Portal" label + logout.
 */
export const PortalLayout: React.FC<PortalLayoutProps> = ({ children }) => {
  const { clearPortalSession } = usePortal();
  const navigate = useNavigate();

  const handleLogout = () => {
    clearPortalSession();
    navigate('/client/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header — branded, no CRM nav */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-3">
          {/* Logo text — replace with <img> if logo asset exists */}
          <span className="text-xl font-bold text-blue-700 tracking-tight">WTF Amplify</span>
          <span className="text-sm text-gray-500 border-l border-gray-300 pl-3">Client Portal</span>
        </div>
        <nav className="flex items-center gap-4">
          <Link to="/client/dashboard" className="text-sm text-gray-600 hover:text-blue-700">
            Dashboard
          </Link>
          <Link to="/client/deliverables" className="text-sm text-gray-600 hover:text-blue-700">
            Deliverables
          </Link>
          <Link to="/client/invoices" className="text-sm text-gray-600 hover:text-blue-700">
            Invoices
          </Link>
          <Link to="/client/payments" className="text-sm text-gray-600 hover:text-blue-700">
            Payments
          </Link>
          <button
            onClick={handleLogout}
            className="text-sm text-gray-500 hover:text-red-600 transition-colors"
          >
            Sign out
          </button>
        </nav>
      </header>

      {/* Main content */}
      <main className="flex-1 max-w-5xl mx-auto w-full px-6 py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 px-6 py-4 text-center text-xs text-gray-400">
        Powered by WTF Amplify — Client Portal
      </footer>
    </div>
  );
};
